import { useT } from '@/i18n/useT';
import { useDeveloperStore } from '@/store/developerStore';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { Panel } from '@/ui/components/Panel';
import styles from './DeveloperPanel.module.css';

const NOT_AVAILABLE = '--';

/**
 * Developer metrics (Phase 1 §16). Only values that really exist are shown: FPS is measured from
 * `requestAnimationFrame`, device counts come from the project. Metrics owned by engines that do
 * not exist yet show `--` instead of invented numbers.
 */
export function DeveloperPanel() {
  const t = useT();
  const frameStats = useDeveloperStore((state) => state.frameStats);
  const deviceCount = useProjectStore((state) => state.project?.scene?.devices?.length ?? 0);
  const speed = useSimulationStore((state) => state.speed);

  const metrics: readonly { id: string; label: string; value: string }[] = [
    {
      id: 'fps',
      label: t('developer.fps'),
      value: frameStats ? String(Math.round(frameStats.fps)) : NOT_AVAILABLE,
    },
    {
      id: 'frameTime',
      label: t('developer.frameTime'),
      value: frameStats ? `${frameStats.frameTimeMs.toFixed(1)} ms` : NOT_AVAILABLE,
    },
    { id: 'plcScan', label: t('developer.plcScan'), value: NOT_AVAILABLE },
    { id: 'simTime', label: t('developer.simulationTime'), value: NOT_AVAILABLE },
    { id: 'speed', label: t('developer.simulationSpeed'), value: `${speed}x` },
    { id: 'devices', label: t('developer.activeDevices'), value: String(deviceCount) },
    { id: 'events', label: t('developer.eventQueue'), value: NOT_AVAILABLE },
    { id: 'render', label: t('developer.renderObjects'), value: String(deviceCount) },
    { id: 'snapshot', label: t('developer.uiSnapshotRate'), value: NOT_AVAILABLE },
  ];

  return (
    <Panel label={t('developer.label')} className={styles.panel}>
      <h2 className={styles.title}>{t('developer.title')}</h2>
      <dl className={styles.metrics}>
        {metrics.map((metric) => (
          <div key={metric.id} className={styles.metric}>
            <dt className={styles.label}>{metric.label}</dt>
            <dd className={styles.value}>{metric.value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}
